import { Chip, Dialog, DialogContent, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Formik } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { COMPANY_PERMISSIONS } from "@/constants/modules.permissions";
import useHasNoPermission from "@/hooks/use-check-permissions";
import { IServiceHoursInput } from "@/interfaces/service-hours.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";

import {
  buildServiceHoursBulkFormSchema,
  buildServiceHoursBulkInitialData,
  mapServiceHoursBulkFormToInputs,
} from "./form-data";
import {
  BUSINESS_WEEKDAY_VALUES,
  isSameWeekDaySelection,
  WEEK_DAY_OPTIONS,
  WEEKDAY_VALUES,
} from "../../constants";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  messages: ConfigurationTabMessages;
  isLoading: boolean;
  onSubmit: (items: IServiceHoursInput[]) => void;
};

function ServiceHoursBulkDialog({ isOpen, onClose, messages, isLoading, onSubmit }: Props) {
  const hasNoPermissionToEdit = useHasNoPermission(COMPANY_PERMISSIONS.EDIT);
  const weekDayOptions = WEEK_DAY_OPTIONS(messages);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent>
        <Formik
          initialValues={buildServiceHoursBulkInitialData()}
          validationSchema={buildServiceHoursBulkFormSchema(messages)}
          onSubmit={(values) => onSubmit(mapServiceHoursBulkFormToInputs(values, messages))}
        >
          {({ values, errors, setFieldValue, submitForm }) => {
            const toggleWeekDay = (weekDay: number) => {
              const isSelected = values.selectedWeekDays.includes(weekDay);
              const nextValue = isSelected
                ? values.selectedWeekDays.filter((day) => day !== weekDay)
                : [...values.selectedWeekDays, weekDay];

              setFieldValue("selectedWeekDays", nextValue);
            };

            const isBusinessDaysSelected = isSameWeekDaySelection(
              values.selectedWeekDays,
              BUSINESS_WEEKDAY_VALUES
            );
            const isAllDaysSelected = isSameWeekDaySelection(
              values.selectedWeekDays,
              WEEKDAY_VALUES
            );

            return (
              <MDBox p={2}>
                <MDTypography variant="h5" fontWeight="bold" mb={3}>
                  {messages.serviceHoursBulkFormTitle}
                </MDTypography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <Paper
                      elevation={0}
                      sx={{ p: 2, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <MDBox>
                          <MDTypography variant="h6">{messages.serviceHoursBulkWeekDaysTitle}</MDTypography>
                          <MDTypography variant="caption" color="text">
                            {messages.serviceHoursBulkWeekDaysDescription}
                          </MDTypography>
                        </MDBox>

                        <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                          <Chip
                            label={messages.serviceHoursBulkSelectBusinessDays}
                            color="default"
                            variant={isBusinessDaysSelected ? "filled" : "outlined"}
                            onClick={() =>
                              setFieldValue("selectedWeekDays", [...BUSINESS_WEEKDAY_VALUES])
                            }
                            sx={{ cursor: "pointer", fontWeight: 500 }}
                          />
                          <Chip
                            label={messages.serviceHoursBulkSelectAllDays}
                            color="default"
                            variant={isAllDaysSelected ? "filled" : "outlined"}
                            onClick={() => setFieldValue("selectedWeekDays", [...WEEKDAY_VALUES])}
                            sx={{ cursor: "pointer", fontWeight: 500 }}
                          />
                          {values.selectedWeekDays.length > 0 && (
                            <Chip
                              label={messages.serviceHoursBulkClearSelection}
                              variant="outlined"
                              onClick={() => setFieldValue("selectedWeekDays", [])}
                              sx={{ cursor: "pointer" }}
                            />
                          )}
                        </MDBox>
                      </MDBox>

                      <MDBox mt={2} display="flex" flexWrap="wrap" gap={1}>
                        {weekDayOptions.map((option) => (
                          <Chip
                            key={option.value}
                            label={option.label}
                            color="default"
                            variant={
                              values.selectedWeekDays.includes(option.value) ? "filled" : "outlined"
                            }
                            onClick={() => toggleWeekDay(option.value)}
                            sx={{ cursor: "pointer" }}
                          />
                        ))}
                      </MDBox>

                      {errors.selectedWeekDays && (
                        <MDTypography variant="caption" color="error" mt={1} display="block">
                          {errors.selectedWeekDays as string}
                        </MDTypography>
                      )}
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Paper
                      elevation={0}
                      sx={{ p: 2, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}
                    >
                      <MDTypography variant="h6">{messages.serviceHoursBulkHoursTitle}</MDTypography>
                      <MDTypography variant="caption" color="text">
                        {messages.serviceHoursBulkHoursDescription}
                      </MDTypography>

                      <Grid container spacing={3} mt={1}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TimePicker
                            label={messages.serviceHoursStartHourLabel}
                            value={values.startOperationHour}
                            onChange={(value) => setFieldValue("startOperationHour", value)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                variant: "standard",
                                error: !!errors.startOperationHour,
                                required: true,
                              },
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                          <TimePicker
                            label={messages.serviceHoursEndHourLabel}
                            value={values.endOperationHour}
                            onChange={(value) => setFieldValue("endOperationHour", value)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                variant: "standard",
                                error: !!errors.endOperationHour,
                                required: true,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>

                <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
                  <MDButton variant="outlined" color="dark" onClick={onClose}>
                    {messages.serviceHoursCancelButton}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={submitForm}
                    isLoading={isLoading}
                    disabled={hasNoPermissionToEdit}
                  >
                    {messages.serviceHoursSaveButton}
                  </MDButton>
                </MDBox>
              </MDBox>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceHoursBulkDialog;
